import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import {
  FaDrum, FaCheckCircle, FaClock, FaUserGraduate,
  FaStar, FaArrowRight, FaMusic
} from 'react-icons/fa';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.15 } } };

const faqs = [
  { q: "What is the prerequisite for the intermediate tabla course?", a: "You should be able to play basic teentaal theka comfortably, know at least 5–8 fundamental bols, and have completed a beginner course or 6+ months of self-study." },
  { q: "How long is the intermediate tabla course?", a: "The intermediate course is a 6-month intensive programme with one-on-one sessions with Maestro Atharva Kagalkar." },
  { q: "What taals will I learn in the intermediate course?", a: "You will learn Keherwa (8 beats), Rupak (7 beats), Jhaptaal (10 beats), Ektaal, and Dadra, along with their theka, variations, and improvisation." },
  { q: "Will I be able to perform after the intermediate course?", a: "Yes. The course culminates in building a 20-minute solo performance set and you will also learn tabla accompaniment for vocalists and classical dancers." },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(({ q, a }) => ({
    "@type": "Question",
    "name": q,
    "acceptedAnswer": { "@type": "Answer", "text": a }
  }))
};

const courseSchema = {
  "@context": "https://schema.org",
  "@type": "Course",
  "name": "Intermediate Tabla Course — Deepen Your Rhythm",
  "description": "An intermediate tabla course by Maestro Atharva Kagalkar at Taalsetu. Build on your foundation with advanced compositions, multiple taals, and performance preparation.",
  "provider": {
    "@type": "MusicSchool",
    "@id": "https://www.taalsetu.com/#organization",
    "name": "Taalsetu Music Academy",
    "sameAs": "https://www.taalsetu.com"
  },
  "instructor": {
    "@type": "Person",
    "name": "Atharva Kagalkar",
    "jobTitle": "Tabla Maestro"
  },
  "url": "https://www.taalsetu.com/courses/intermediate/",
  "image": "https://www.taalsetu.com/logo512.png",
  "courseMode": ["online", "onsite"],
  "educationalLevel": "Intermediate",
  "inLanguage": "hi-IN",
  "availableLanguage": ["Hindi", "English", "Marathi"],
  "offers": {
    "@type": "Offer",
    "category": "Paid",
    "availability": "https://schema.org/InStock"
  }
};

const curriculum = [
  { week: "Month 1", topic: "Advanced Bols & Speed Building", desc: "Kinnar, Dhit, Ghinn — expanding your vocabulary of strokes. Speed exercises using layakari." },
  { week: "Month 2", topic: "Multiple Taals", desc: "Keherwa (8 beats), Rupak (7 beats), Jhaptaal (10 beats) — theka, variations, and improvisation." },
  { week: "Month 3", topic: "Rela & Tukda", desc: "Fast-paced rela compositions and short tukdas for concert performance." },
  { week: "Month 4", topic: "Peshkar & Kaida Elaboration", desc: "Traditional peshkar style playing — the elegant introduction form of tabla solo." },
  { week: "Month 5", topic: "Accompanying Vocalists", desc: "How to play tabla as accompaniment to khayal, bhajan, and classical dance forms." },
  { week: "Month 6", topic: "Solo Performance Preparation", desc: "Building a 20-minute solo set — structure, flow, and audience presentation." },
];

const IntermediateCourse = () => {
  return (
    <div className="min-h-screen font-sans bg-white dark:bg-maroon-dark">
      <SEO
        title="Intermediate Tabla Course Online — Deepen Your Rhythm | Taalsetu"
        description="Take your tabla skills to the next level with Taalsetu's intermediate course. Learn multiple taals, rela, kaida elaboration, and performance techniques with Maestro Atharva Kagalkar."
        keywords="intermediate tabla course, tabla classes intermediate, advanced tabla bols, tabla taals online, keherwa jhaptaal tabla, tabla performance training, Taalsetu intermediate, Atharva Kagalkar tabla lessons"
        url="https://www.taalsetu.com/courses/intermediate/"
        schemaMarkup={[courseSchema, faqSchema]}
        breadcrumbs={[
          { name: 'Home', url: 'https://www.taalsetu.com/' },
          { name: 'Intermediate Tabla Course', url: 'https://www.taalsetu.com/courses/intermediate/' }
        ]}
      />

      {/* Hero */}
      <motion.section
        className="relative pt-28 pb-20 bg-gradient-to-br from-maroon via-maroon to-maroon-dark text-white overflow-hidden"
        initial="hidden" animate="visible" variants={fadeUp}
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-20 w-72 h-72 bg-gold rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-10 w-48 h-48 bg-gold rounded-full blur-3xl" />
        </div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="inline-flex items-center bg-gold/20 border border-gold/30 text-gold px-4 py-2 rounded-full text-sm font-medium mb-6">
            <FaMusic className="mr-2" /> Intermediate Level
          </div>
          <h1 className="font-serif text-4xl md:text-6xl font-bold leading-tight mb-6">
            Intermediate Tabla —<br />
            <span className="text-gold">Deepen Your Rhythm</span>
          </h1>
          <p className="text-lg md:text-xl text-gold-light/80 max-w-2xl mb-10 leading-relaxed">
            You know the basics. Now master multiple taals, complex compositions, rela, and prepare for your first public performance. A 6-month intensive with Maestro Atharva Kagalkar.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/register" className="btn-gold flex items-center !rounded-xl text-lg px-8 py-4">
              Enrol Now <FaArrowRight className="ml-2" />
            </Link>
            <a href="tel:+918275664288" className="flex items-center px-8 py-4 border border-gold/40 text-gold-light rounded-xl hover:bg-gold/10 transition-all duration-300 font-medium text-lg">
              Book Free Assessment
            </a>
          </div>

          <div className="flex flex-wrap gap-8 mt-12">
            {[
              { icon: <FaClock />, label: "6 Month Course" },
              { icon: <FaUserGraduate />, label: "Prior Basics Required" },
              { icon: <FaStar />, label: "Performance Focused" },
              { icon: <FaDrum />, label: "Online & Offline" },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-gold-light/70 text-sm">
                <span className="text-gold">{item.icon}</span> {item.label}
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Prerequisites */}
      <motion.section
        className="py-16 bg-gold/5 border-y border-gold/10"
        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-2xl font-bold text-maroon dark:text-gold-light mb-6 text-center">
            Prerequisites — Who Is This Course For?
          </h2>
          <div className="grid md:grid-cols-2 gap-4 max-w-3xl mx-auto">
            {[
              "You can play basic teentaal theka comfortably",
              "You know at least 5–8 fundamental bols",
              "You've completed a beginner course or 6+ months of self-study",
              "You want to perform or accompany other musicians",
            ].map((point, i) => (
              <div key={i} className="flex items-start gap-3">
                <FaCheckCircle className="text-gold mt-1 flex-shrink-0" />
                <span className="text-maroon/70 dark:text-gold-light/70 text-sm">{point}</span>
              </div>
            ))}
          </div>
          <p className="text-center mt-6 text-sm text-maroon/50 dark:text-gold-light/50">
            Not sure if you qualify?{" "}
            <a href="tel:+918275664288" className="text-gold hover:underline">Call us for a free 15-minute assessment.</a>
          </p>
        </div>
      </motion.section>

      {/* What you'll learn */}
      <motion.section
        className="py-20 bg-white dark:bg-maroon-dark"
        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-14" variants={fadeUp}>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-maroon dark:text-gold-light mb-4">What You'll Master</h2>
            <div className="gold-divider mb-4" />
          </motion.div>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              "Keherwa, Rupak, Jhaptaal, Ektaal & Dadra taals",
              "Rela — the fast, flowing composition form",
              "Tukda and tihai (rhythmic cadences)",
              "Peshkar style — the classical introduction form",
              "Layakari — playing in duple, triple, and quadruple subdivisions",
              "Improvisation within a taal framework",
              "Tabla accompaniment for vocalists and dancers",
              "Building and presenting a solo performance set",
            ].map((point, i) => (
              <motion.div key={i} variants={fadeUp} className="flex items-start gap-3">
                <FaCheckCircle className="text-gold mt-1 flex-shrink-0 text-lg" />
                <span className="text-maroon/70 dark:text-gold-light/70">{point}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Curriculum */}
      <motion.section
        className="py-20 bg-maroon/5 dark:bg-maroon-dark/50"
        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-14" variants={fadeUp}>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-maroon dark:text-gold-light mb-4">6-Month Curriculum</h2>
            <div className="gold-divider mb-4" />
          </motion.div>
          <div className="space-y-4">
            {curriculum.map((item, i) => (
              <motion.div key={i} variants={fadeUp}
                className="bg-white dark:bg-maroon-dark border border-gold/10 rounded-xl p-6 flex gap-6 hover:border-gold/30 transition-all duration-300"
              >
                <div className="w-28 flex-shrink-0">
                  <span className="text-xs font-bold text-gold uppercase tracking-wider">{item.week}</span>
                </div>
                <div>
                  <h3 className="font-serif text-lg font-bold text-maroon dark:text-gold-light mb-1">{item.topic}</h3>
                  <p className="text-sm text-maroon/60 dark:text-gold-light/60 leading-relaxed">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* FAQs */}
      <motion.section
        className="py-20 bg-white dark:bg-maroon-dark"
        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-14" variants={fadeUp}>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-maroon dark:text-gold-light mb-4">
              Frequently Asked Questions
            </h2>
            <div className="gold-divider" />
          </motion.div>
          <div className="space-y-5">
            {faqs.map((faq, i) => (
              <motion.div key={i} variants={fadeUp} className="border border-gold/15 rounded-xl p-6 bg-maroon/3 dark:bg-white/5">
                <h3 className="font-serif font-bold text-maroon dark:text-gold-light mb-2">{faq.q}</h3>
                <p className="text-maroon/65 dark:text-gold-light/65 text-sm leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Further Reading */}
      <motion.section
        className="py-16 bg-white dark:bg-maroon-dark border-t border-gold/10"
        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-2xl font-bold text-maroon dark:text-gold-light mb-6">Further Reading</h2>
          <Link
            to="/blog/intermediate-tabla-tips/"
            className="group flex flex-col sm:flex-row gap-4 bg-maroon/4 dark:bg-white/5 border border-gold/15 rounded-xl p-6 hover:border-gold/40 transition-all duration-300"
          >
            <div className="flex-shrink-0 w-10 h-10 bg-gold/20 rounded-lg flex items-center justify-center text-gold">
              <FaMusic />
            </div>
            <div>
              <p className="text-xs font-bold text-gold uppercase tracking-wider mb-1">Intermediate Guide</p>
              <p className="font-serif font-bold text-maroon dark:text-gold-light group-hover:text-gold transition-colors">How to Improve Your Tabla Playing — Tips for Intermediate Students</p>
              <p className="text-sm text-maroon/60 dark:text-gold-light/60 mt-1">Rela practice, layakari drills, breaking plateaus, and preparing for your first performance.</p>
            </div>
          </Link>
        </div>
      </motion.section>

      {/* CTA */}
      <motion.section
        className="py-20 bg-maroon text-white text-center"
        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
      >
        <div className="max-w-2xl mx-auto px-4">
          <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4">Take Your Tabla to the Next Level</h2>
          <p className="text-gold-light/70 mb-8 text-lg">Join students performing at concerts, festivals, and cultural events worldwide.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/register" className="btn-gold !rounded-xl px-10 py-4 text-lg flex items-center">
              Enrol Now <FaArrowRight className="ml-2" />
            </Link>
            <Link to="/#contact" className="px-10 py-4 border border-gold/40 text-gold-light rounded-xl hover:bg-gold/10 transition-all duration-300 text-lg">
              Contact Us
            </Link>
          </div>
        </div>
      </motion.section>
    </div>
  );
};

export default IntermediateCourse;
